import { useEffect, useState } from 'react';
import ReactImageLightbox from 'react-image-lightbox';
import { get } from '@alltrails/shared/api';
import CreditLinkResponse from 'types/CreditLinkResponse';
import AdInterstitial from 'components/AdInterstitial';
import useExperiment from 'hooks/useExperiment';
import Experiments from '@alltrails/shared/types/Experiments';
import PhotoCreditLink from '../PhotoCreditLink';

import 'react-image-lightbox/style.css'; // this is an import for the plugin
import './styles/overrides.scss';

type Props = {
  handleCloseRequest: () => void;
  onPhotoViewed?: (photoId: number) => void;
  images: { id?: number; url: string }[];
  listId?: number;
  mapId?: number;
  showCreditActions?: boolean;
  startingIndex: number;
  trailId?: number;
};

function ImageCaption({ url }: { url: string }) {
  const [photoCreditProps, setPhotoCreditProps] = useState(null);

  useEffect(() => {
    async function fetchCreditLink() {
      try {
        setPhotoCreditProps(null); // there is some state in PhotoCreditLink so we need to blow away the component to get a full update
        const response = await get<CreditLinkResponse>(url);
        setPhotoCreditProps(response);
      } catch (e) {
        setPhotoCreditProps(null);
      }
    }

    fetchCreditLink();
  }, [url]);

  return <div className="credit-link-container">{photoCreditProps && <PhotoCreditLink {...photoCreditProps} />}</div>;
}

function LightboxAd({ count, experiment }: { count: number; experiment: Experiments['web-growth-native-ads-v2']['value'] }) {
  if (count === 5) {
    return <AdInterstitial adId="gpt-photos1" adUnitPath="/4689141/alltrails_photos1_300x250" experiment={experiment} />;
  }

  if (count === 15) {
    return <AdInterstitial adId="gpt-photos2" adUnitPath="/4689141/alltrails_photos2_300x250" experiment={experiment} />;
  }

  if (count === 25) {
    return <AdInterstitial adId="gpt-photos3" adUnitPath="/4689141/alltrails_photos3_300x250" experiment={experiment} />;
  }

  return null;
}

function Lightbox({ handleCloseRequest, onPhotoViewed, images, listId, mapId, showCreditActions, startingIndex, trailId }: Props) {
  const [photoIndex, setPhotoIndex] = useState(startingIndex);
  const [count, setCount] = useState(0);
  const experiment = useExperiment('web-growth-native-ads-v2');

  useEffect(() => {
    document.querySelector('html').classList.add('modalOpen');

    return () => {
      document.querySelector('html').classList.remove('modalOpen');
    };
  }, []);

  useEffect(() => {
    onPhotoViewed?.(images[photoIndex].id);
  }, [photoIndex]);

  function handleOnMovePrevRequest() {
    const imagesLength = images.length;
    setPhotoIndex((photoIndex + imagesLength - 1) % imagesLength);
    setCount(count + 1);
  }

  function handleOnMoveNextRequest() {
    const imageLength = images.length;
    setPhotoIndex((photoIndex + 1) % imageLength);
    setCount(count + 1);
  }

  const customStyles = {
    overlay: {
      zIndex: 1005
    },
    captionContent: {
      width: '100%'
    }
  };

  let url = `/photos/${images[photoIndex].id}/credit-link`;

  const query = showCreditActions ? '&' : '?';
  if (showCreditActions) {
    url = `${url}?showActions=${showCreditActions}`;
  }

  if (trailId) {
    url = `${url}${query}provide_set_as_default=true`;
  } else if (mapId) {
    url = `${url}${query}provide_map_id=${mapId}`;
  } else if (listId) {
    url = `${url}${query}provide_list_id=${listId}`;
  } else {
    url = `${url}${query}provide_trail_links=true`;
  }

  return (
    <>
      <ReactImageLightbox
        reactModalStyle={customStyles}
        wrapperClassName="react-image-lightbox"
        mainSrc={images[photoIndex].url}
        nextSrc={images[(photoIndex + 1) % images.length].url}
        prevSrc={images[(photoIndex + images.length - 1) % images.length].url}
        onCloseRequest={handleCloseRequest}
        onMovePrevRequest={handleOnMovePrevRequest}
        onMoveNextRequest={handleOnMoveNextRequest}
        imageCaption={<ImageCaption url={url} />}
      />
      {(experiment?.value === 'unpaid_only' || experiment?.value === 'unpaid-and-paid') && (
        <LightboxAd count={count} experiment={experiment?.value} />
      )}
    </>
  );
}

export default Lightbox;
